import { Injectable } from '@angular/core';
import { createEffect, Actions, ofType } from '@ngrx/effects';
import { fetch } from '@nrwl/angular';
import { GMServiceVisitByVinQueryParameters } from '@quorum/models/oem-query';
import { GMServiceVisitDtcCount } from 'libs/models/oem-resource/gm/service-visit-dtc-count.model';
import { GMServiceVisitDtc } from 'libs/models/oem-resource/gm/service-visit-dtc.model';
import { GMServiceVisitVehicleData } from 'libs/models/oem-resource/gm/service-visit-vehicle-data.model';
import { OemIntegrationService } from '@quorum/quorum-api';
import { catchError, concatMap, delay, map, retry, retryWhen, take, tap } from 'rxjs/operators';
import { Observable, of, throwError } from 'rxjs';
import * as SvcVehicleDiagnosticsRootActions from './svc-vehicle-diagnostics-root.actions';
import * as SvcVehicleDiagnosticsRootFeature from './svc-vehicle-diagnostics-root.reducer';
import { HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { GMServiceVisitByDealer, GMServiceVisitByVin } from '@quorum/models/oem-resource';

@Injectable()
export class SvcVehicleDiagnosticsRootEffects {
  init$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SvcVehicleDiagnosticsRootActions.init),
      fetch({
        run: (action) => {
          // Your custom service 'load' logic goes here. For now just return a success action...
          return SvcVehicleDiagnosticsRootActions.loadSvcVehicleDiagnosticsRootSuccess({
            svcVehicleDiagnosticsRoot: [],
          });
        },
        onError: (action, error) => {
          console.error('Error', error);
          return SvcVehicleDiagnosticsRootActions.loadSvcVehicleDiagnosticsRootFailure({ error });
        },
      })
    )
  );

  getGMServiceVisitByVin = createEffect(() =>
    this.actions$.pipe(
      ofType(SvcVehicleDiagnosticsRootActions.getGMServiceVisitByVin),
      fetch({
        run: (action) => {
          //hardcoding version this is determined by GM.
          let headers: HttpHeaders = new HttpHeaders();
          headers = headers.append('quorum-business-id', action.businessId);
          const specVersion = '1.0';
          return this.oemIntegrationService
            .get(
              `gm-savi/get-service-visit-by-vin/${action.vin}/country/${action.dealerCountryCode}/language/en-US/version/${specVersion}`,
              {
                headers: headers,
                params: action?.queryParameters,
              }
            )
            .pipe(
              delay(2000),
              this.handleRetryError(2000),
              map((serviceVisits: GMServiceVisitByVin[]) => {
                return SvcVehicleDiagnosticsRootActions.getGMServiceVisitByVinSuccess({
                  businessId: action.businessId,
                  dealerCountryCode: action.dealerCountryCode,
                  serviceVisits: serviceVisits,
                });
              })
            );
        },
        onError: (action, error) => {
          return SvcVehicleDiagnosticsRootActions.getGMServiceVisitByVinError({
            businessId: action.businessId,
            dealerCountryCode: action.dealerCountryCode,
            vin: action.vin,
            queryParameters: action.queryParameters,
            error: error,
          });
        },
      })
    )
  );

  getGMServiceVisitByDealer = createEffect(() =>
    this.actions$.pipe(
      ofType(SvcVehicleDiagnosticsRootActions.getGMServiceVisitByDealer),
      fetch({
        run: (action) => {
          //hardcoding version this is determined by GM.
          let headers: HttpHeaders = new HttpHeaders();
          headers = headers.append('quorum-business-id', action.businessId);
          const specVersion = '1.0';
          return this.oemIntegrationService
            .get(
              `gm-savi/get-service-visit-by-dealer/start/${action.startDate}/end/${action.endDate}/bac/${action.bac}/country/${action.dealerCountryCode}/language/en-US/version/${specVersion}`,
              {
                headers: headers,
                params: action?.queryParameters,
              }
            )
            .pipe(
              map((serviceVisits: GMServiceVisitByDealer[]) => {
                return SvcVehicleDiagnosticsRootActions.getGMServiceVisitByDealerSuccess({
                  businessId: action.businessId,
                  dealerCountryCode: action.dealerCountryCode,
                  startDate: action.startDate,
                  endDate: action.endDate,
                  bac: action.bac,
                  serviceVisits: serviceVisits,
                });
              })
            );
        },
        onError: (action, error) => {
          return SvcVehicleDiagnosticsRootActions.getGMServiceVisitByDealerError({
            businessId: action.businessId,
            dealerCountryCode: action.dealerCountryCode,
            startDate: action.startDate,
            endDate: action.endDate,
            bac: action.bac,
            queryParameters: action.queryParameters,
            error: error,
          });
        },
      })
    )
  );

  getLinkedServiceVisitDtc = createEffect(() =>
    this.actions$.pipe(
      ofType(SvcVehicleDiagnosticsRootActions.getLinkedServiceVisitDtc),
      fetch({
        run: (action) => {
          //hardcoding version this is determined by GM.
          let headers: HttpHeaders = new HttpHeaders();
          headers = headers.append('quorum-business-id', action.businessId);
          const specVersion = '1.0';
          return this.oemIntegrationService
            .get(
              `gm-savi/get-service-visit-dtc/id/${action.serviceVisitId}/country/${action.dealerCountryCode}/language/en-US/version/${specVersion}`,
              { headers: headers }
            )
            .pipe(
              map((linkedServiceVisitDtc: GMServiceVisitDtc[]) => {
                return SvcVehicleDiagnosticsRootActions.getLinkedServiceVisitDtcSuccess({
                  linkedServiceVisitDtc: linkedServiceVisitDtc,
                });
              })
            );
        },
        onError: (action, error) => {
          return SvcVehicleDiagnosticsRootActions.getLinkedServiceVisitDtcError({
            error: error,
          });
        },
      })
    )
  );

  getLinkedServiceVisitMpvi = createEffect(() =>
    this.actions$.pipe(
      ofType(SvcVehicleDiagnosticsRootActions.getLinkedServiceVisitMpvi),
      fetch({
        run: (action) => {
          //hardcoding version this is determined by GM.
          let headers: HttpHeaders = new HttpHeaders();
          headers = headers.append('quorum-business-id', action.businessId);
          const specVersion = '1.0';
          return this.oemIntegrationService
            .get(
              `gm-savi/get-service-visit-vehicle-data/id/${action.serviceVisitId}/country/${action.dealerCountryCode}/language/en-US/version/${specVersion}`,
              { headers: headers }
            )
            .pipe(
              map((linkedServiceVisitMpvi: GMServiceVisitVehicleData[]) => {
                return SvcVehicleDiagnosticsRootActions.getLinkedServiceVisitMpviSuccess({
                  linkedServiceVisitMpvi: linkedServiceVisitMpvi,
                });
              })
            );
        },
        onError: (action, error) => {
          return SvcVehicleDiagnosticsRootActions.getLinkedServiceVisitMpviError({
            error: error,
          });
        },
      })
    )
  );

  getLinkedServiceVisitEcu = createEffect(() =>
    this.actions$.pipe(
      ofType(SvcVehicleDiagnosticsRootActions.getLinkedServiceVisitEcu),
      fetch({
        run: (action) => {
          //hardcoding version this is determined by GM.
          let headers: HttpHeaders = new HttpHeaders();
          headers = headers.append('quorum-business-id', action.businessId);
          const specVersion = '1.0';
          return this.oemIntegrationService
            .get(
              `gm-savi/get-service-visit-dtc-count/id/${action.serviceVisitId}/country/${action.dealerCountryCode}/language/en-US/version/${specVersion}`,
              { headers: headers }
            )
            .pipe(
              map((linkedServiceVisitEcu: GMServiceVisitDtcCount[]) => {
                return SvcVehicleDiagnosticsRootActions.getLinkedServiceVisitEcuSuccess({
                  linkedServiceVisitEcu: linkedServiceVisitEcu,
                });
              })
            );
        },
        onError: (action, error) => {
          return SvcVehicleDiagnosticsRootActions.getLinkedServiceVisitEcuError({
            error: error,
          });
        },
      })
    )
  );

  handleRetryError(delayTime: number) {
    let retries = 0;
    const exceedAttemptLimit = 3;
    return retryWhen((error) => {
      return error.pipe(
        delay(delayTime),
        concatMap((err) => {
          retries = retries + 1;
          if (retries < exceedAttemptLimit) {
            return of(err);
          } else {
            throw err;
          }
        })
      );
    });
  }

  constructor(private readonly actions$: Actions, private oemIntegrationService: OemIntegrationService) {}
}
